import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Stack, Typography } from "@mui/material";
import { OnboardingQuestion } from "store/types/user";

interface DynamicStepProps {
  step: number;
  totalSteps: number;
  title: string;
  questions: OnboardingQuestion[];
  answers: { [questionIndex: number]: string | string[] };
  onAnswer: (questionIndex: number, value: string | string[]) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const DynamicStep: React.FC<DynamicStepProps> = ({
  step,
  totalSteps,
  title,
  questions,
  answers,
  onAnswer,
  onNext,
  onBack,
  onSkip
}) => {
  const handleToggle = (questionIndex: number, value: string, multiple: boolean) => {
    const currentAnswer = answers[questionIndex];

    if (multiple) {
      const currentArray = Array.isArray(currentAnswer) ? currentAnswer : [];
      const newAnswer = currentArray.includes(value)
        ? currentArray.filter((v) => v !== value)
        : [...currentArray, value];
      onAnswer(questionIndex, newAnswer);
    } else {
      onAnswer(questionIndex, value);
    }
  };

  const isAnswered = (questionIndex: number, value: string): boolean => {
    const answer = answers[questionIndex];
    if (Array.isArray(answer)) {
      return answer.includes(value);
    }
    return answer === value;
  };

  const canContinue = questions.every((_, index) => {
    const answer = answers[index];
    if (answer === undefined || answer === null || answer === "") {
      return false;
    }
    if (Array.isArray(answer) && answer.length === 0) {
      return false;
    }
    return true;
  });

  // Group options by group field
  const getGroupedOptions = (question: OnboardingQuestion) => {
    const grouped: { [group: string]: typeof question.options } = {};
    question.options.forEach((option) => {
      const groupKey = option.group || "_default";
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(option);
    });
    return grouped;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Back button - top left */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14, color: "#26a6ae" }} />}
        sx={{
          position: "absolute",
          top: 24,
          left: 32,
          color: "#26a6ae",
          textTransform: "none",
          fontSize: "14px",
          minWidth: "auto",
          p: 0.5,
          "&:hover": {
            opacity: 0.8
          }
        }}
      >
        Back
      </Button>

      {/* Skip and Step counter - top right */}
      <Stack
        direction="row"
        gap="16px"
        alignItems="center"
        sx={{
          position: "absolute",
          top: 24,
          right: 32
        }}
      >
        <Button
          onClick={onSkip}
          sx={{
            color: "rgba(10, 49, 66, 0.6)",
            textTransform: "none",
            fontSize: "14px",
            minWidth: "auto",
            p: 0.5,
            "&:hover": {
              bgcolor: "transparent",
              color: "#071f35"
            }
          }}
        >
          Skip this step
        </Button>
        <Box
          sx={{
            width: "1px",
            height: "14px",
            bgcolor: "rgba(10, 49, 66, 0.15)"
          }}
        />
        <Box
          sx={{
            fontSize: "13px",
            color: "rgba(10, 49, 66, 0.5)",
            fontWeight: 500
          }}
        >
          Step {step} of {totalSteps}
        </Box>
      </Stack>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pt: 8,
          pb: 6,
          px: 3
        }}
      >
        <Box sx={{ maxWidth: "720px", width: "100%" }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: "28px",
              fontWeight: 500,
              color: "#0a3142",
              mb: 1
            }}
          >
            {title}
          </Typography>

          {questions.map((question, qIndex) => {
            const groupedOptions = getGroupedOptions(question);
            const hasGroups = Object.keys(groupedOptions).length > 1 || !groupedOptions._default;

            return (
              <Box key={qIndex} sx={{ mb: qIndex < questions.length - 1 ? 4 : 0 }}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    color: "rgba(10, 49, 66, 0.6)",
                    mb: 4
                  }}
                >
                  {question.question}
                </Typography>

                {question.type === "card_list" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: 2,
                      mb: 4
                    }}
                  >
                    {question.options.map((option) => (
                      <Button
                        key={option.value}
                        onClick={() => handleToggle(qIndex, option.value, question.multiple)}
                        sx={{
                          height: "auto",
                          p: 2.5,
                          border: "1.5px solid",
                          borderColor: isAnswered(qIndex, option.value) ? "#26a6ae" : "rgba(10, 49, 66, 0.12)",
                          borderRadius: "10px",
                          bgcolor: "#fff",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          textAlign: "left",
                          textTransform: "none",
                          transition: "all 0.15s",
                          "&:hover": {
                            borderColor: "#7b92a9"
                          }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: 500,
                              color: "#0a3142",
                              mb: 0.5
                            }}
                          >
                            {option.title}
                          </Typography>
                          {option.description && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "rgba(10, 49, 66, 0.5)"
                              }}
                            >
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                        {isAnswered(qIndex, option.value) && (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              bgcolor: "#26a6ae",
                              color: "#fff",
                              fontSize: "11px",
                              ml: 1,
                              flexShrink: 0
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 15 }} />
                          </Box>
                        )}
                      </Button>
                    ))}
                  </Box>
                )}

                {question.type === "chips" && (
                  <>
                    {hasGroups ? (
                      Object.entries(groupedOptions).map(([groupName, options]) => (
                        <Box key={groupName} sx={{ mb: 4 }}>
                          {groupName !== "_default" && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "rgba(10, 49, 66, 0.5)",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                mb: 1.5
                              }}
                            >
                              {groupName}
                            </Typography>
                          )}
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                            {options.map((option) => (
                              <Button
                                key={option.value}
                                onClick={() => handleToggle(qIndex, option.value, question.multiple)}
                                sx={{
                                  px: 2.5,
                                  py: 1.25,
                                  border: "1px solid",
                                  borderColor: isAnswered(qIndex, option.value) ? "#26a6ae" : "#7b92a9",
                                  borderRadius: "20px",
                                  bgcolor: isAnswered(qIndex, option.value) ? "#26a6ae" : "#fff",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: isAnswered(qIndex, option.value) ? "#fff" : "#0a3142",
                                  textTransform: "none",
                                  minWidth: question.multiple ? "auto" : "90px",
                                  "&:hover": {
                                    borderColor: isAnswered(qIndex, option.value) ? "#1e8c94" : "#071f35",
                                    bgcolor: isAnswered(qIndex, option.value) ? "#1e8c94" : "#fff"
                                  }
                                }}
                              >
                                {option.title}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
                        {question.options.map((option) => (
                          <Button
                            key={option.value}
                            onClick={() => handleToggle(qIndex, option.value, question.multiple)}
                            sx={{
                              px: 2.5,
                              py: 1.25,
                              border: "1px solid",
                              borderColor: isAnswered(qIndex, option.value) ? "#26a6ae" : "#7b92a9",
                              borderRadius: "20px",
                              bgcolor: isAnswered(qIndex, option.value) ? "#26a6ae" : "#fff",
                              fontSize: "14px",
                              fontWeight: 500,
                              color: isAnswered(qIndex, option.value) ? "#fff" : "#0a3142",
                              textTransform: "none",
                              minWidth: question.multiple ? "auto" : "90px",
                              "&:hover": {
                                borderColor: isAnswered(qIndex, option.value) ? "#1e8c94" : "#071f35",
                                bgcolor: isAnswered(qIndex, option.value) ? "#1e8c94" : "#fff"
                              }
                            }}
                          >
                            {option.title}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            );
          })}

          {/* Actions */}
          <Stack direction="column" gap="12px" alignItems="center" justifyContent="center">
            <Button
              variant="contained"
              onClick={onNext}
              disabled={!canContinue}
              sx={{
                width: "100px",
                bgcolor: "#071f35",
                boxShadow: "none",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#0f2d47"
                },
                "&:disabled": {
                  bgcolor: "rgba(10, 49, 66, 0.12)",
                  color: "rgba(255, 255, 255, 0.6)"
                }
              }}
            >
              Continue
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
